import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { adminApi } from '../../services/adminApi';
import './AdminLogs.css';

function AdminLogs() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionFilter, setActionFilter] = useState('all');
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 0
    });

    const loadLogs = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit
            };
            if (actionFilter !== 'all') params.action = actionFilter;

            const res = await adminApi.getActivityLogs(params);
            setLogs(res.data.logs || []);
            setPagination(prev => ({
                ...prev,
                total: res.data.pagination?.total || 0,
                totalPages: res.data.pagination?.total_pages || 0
            }));
        } catch (err) {
            setError('Erreur lors du chargement des logs');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.limit, actionFilter]);

    useEffect(() => {
        loadLogs();
    }, [loadLogs]);

    const handleActionFilter = (e) => {
        setActionFilter(e.target.value);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('fr-FR');
    };

    const getActionBadgeClass = (action) => {
        if (action.includes('delete')) return 'danger';
        if (action.includes('create')) return 'success';
        if (action.includes('update')) return 'warning';
        if (action.includes('login')) return 'info';
        return 'default';
    };

    const formatAction = (action) => {
        const actions = {
            'login': 'Connexion',
            'logout': 'Deconnexion',
            'view_organization': 'Vue organisation',
            'update_organization': 'Maj organisation',
            'delete_organization': 'Suppression organisation',
            'toggle_organization_status': 'Toggle statut org',
            'create_template': 'Creation template',
            'update_template': 'Maj template',
            'delete_template': 'Suppression template',
            'view_stats': 'Vue statistiques',
            'view_health': 'Vue sante serveur'
        };
        return actions[action] || action;
    };

    return (
        <AdminLayout>
            <div className="admin-logs">
                <div className="admin-page-header">
                    <h1>Logs d'activite</h1>
                    <p>Historique des actions des administrateurs</p>
                </div>

                {/* Filters */}
                <div className="admin-filters">
                    <select
                        className="admin-select"
                        value={actionFilter}
                        onChange={handleActionFilter}
                    >
                        <option value="all">Toutes les actions</option>
                        <option value="login">Connexions</option>
                        <option value="view_organization">Vues organisations</option>
                        <option value="update_organization">Modifications organisations</option>
                        <option value="delete_organization">Suppressions organisations</option>
                        <option value="create_template">Creations templates</option>
                        <option value="update_template">Modifications templates</option>
                        <option value="delete_template">Suppressions templates</option>
                    </select>
                    <span className="admin-result-count">
                        {pagination.total} entree{pagination.total > 1 ? 's' : ''}
                    </span>
                </div>

                {/* Table */}
                <div className="admin-card">
                    {loading ? (
                        <div className="admin-loading-inline">Chargement...</div>
                    ) : error ? (
                        <div className="admin-error">{error}</div>
                    ) : logs.length === 0 ? (
                        <p className="no-data">Aucun log trouve</p>
                    ) : (
                        <table className="admin-table logs-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Admin</th>
                                    <th>Action</th>
                                    <th>Entite</th>
                                    <th>Details</th>
                                    <th>IP</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map(log => (
                                    <tr key={log.id}>
                                        <td className="log-date">
                                            {formatDateTime(log.created_at)}
                                        </td>
                                        <td>
                                            <span className="log-admin">
                                                {log.admin_name || `Admin #${log.admin_id}`}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`action-badge ${getActionBadgeClass(log.action)}`}>
                                                {formatAction(log.action)}
                                            </span>
                                        </td>
                                        <td>
                                            {log.entity_type && (
                                                <span className="entity-info">
                                                    {log.entity_type}
                                                    {log.entity_id && ` #${log.entity_id}`}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            {log.details && (
                                                <span className="log-details" title={JSON.stringify(log.details)}>
                                                    {typeof log.details === 'object'
                                                        ? Object.entries(log.details).slice(0, 2).map(([k, v]) => `${k}: ${v}`).join(', ')
                                                        : log.details}
                                                </span>
                                            )}
                                        </td>
                                        <td className="log-ip">
                                            {log.ip_address || '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <div className="admin-pagination">
                            <button
                                onClick={() => handlePageChange(pagination.page - 1)}
                                disabled={pagination.page === 1}
                            >
                                Precedent
                            </button>
                            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                let pageNum;
                                if (pagination.totalPages <= 5) {
                                    pageNum = i + 1;
                                } else if (pagination.page <= 3) {
                                    pageNum = i + 1;
                                } else if (pagination.page >= pagination.totalPages - 2) {
                                    pageNum = pagination.totalPages - 4 + i;
                                } else {
                                    pageNum = pagination.page - 2 + i;
                                }
                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => handlePageChange(pageNum)}
                                        className={pagination.page === pageNum ? 'active' : ''}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                            <button
                                onClick={() => handlePageChange(pagination.page + 1)}
                                disabled={pagination.page === pagination.totalPages}
                            >
                                Suivant
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
}

export default AdminLogs;
